// src/components/SiteFilterSelector.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Skeleton,
  Divider,
  Chip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Check as CheckIcon,
  Business as AllSitesIcon,
} from '@mui/icons-material';
import { useSiteFilter } from '../contexts/SiteFilterContext';

const SiteFilterSelector = () => {
  const {
    sites,
    selectedSiteId,
    loading,
    canViewAllSites,
    selectSite,
    selectedSiteName,
  } = useSiteFilter();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSiteSelect = (siteId) => {
    selectSite(siteId);
    handleClose();
  };

  // Don't render if user has no sites and no "all" option
  // Show the switcher even with 1 site so the user sees which site they're scoped to
  if (!loading && sites.length < 1 && !canViewAllSites) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        <Skeleton variant="rounded" width={140} height={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
      <Button
        id="site-filter-button"
        aria-controls={open ? 'site-filter-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        variant="outlined"
        size="small"
        startIcon={<LocationIcon />}
        endIcon={<ArrowDownIcon />}
        sx={{
          color: 'white',
          borderColor: 'rgba(255, 255, 255, 0.5)',
          textTransform: 'none',
          minWidth: 120,
          maxWidth: 200,
          '&:hover': {
            borderColor: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 120,
          }}
        >
          {selectedSiteName}
        </Typography>
      </Button>

      <Menu
        id="site-filter-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'site-filter-button',
        }}
        PaperProps={{
          sx: {
            maxHeight: 400,
            minWidth: 200,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Filter by Site
          </Typography>
        </Box>
        <Divider />

        {/* All Sites option (admin only) */}
        {canViewAllSites && (
          <MenuItem
            onClick={() => handleSiteSelect('all')}
            selected={selectedSiteId === 'all'}
          >
            <ListItemIcon>
              {selectedSiteId === 'all' ? (
                <CheckIcon fontSize="small" color="primary" />
              ) : (
                <AllSitesIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              All Sites
              <Chip
                label="Admin"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
              />
            </ListItemText>
          </MenuItem>
        )}

        {canViewAllSites && sites.length > 0 && <Divider />}

        {/* Individual sites */}
        {sites.map((site) => {
          const siteId = String(site.id || site._id);
          const isSelected = String(selectedSiteId) === siteId;

          return (
            <MenuItem
              key={siteId}
              onClick={() => handleSiteSelect(siteId)}
              selected={isSelected}
            >
              <ListItemIcon>
                {isSelected ? (
                  <CheckIcon fontSize="small" color="primary" />
                ) : (
                  <LocationIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={site.friendlyName || site.name}
                secondary={site.locationTypeModel?.name || null}
                primaryTypographyProps={{
                  sx: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </MenuItem>
          );
        })}

        {sites.length === 0 && (
          <MenuItem disabled>
            <ListItemText secondary="No sites available" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default SiteFilterSelector;
